import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { DesktopNavigation } from "./navbar/DesktopNavigation";
import { AuthSection } from "./navbar/AuthSection";
import { MobileNavDrawer } from "./navbar/MobileNavDrawer";
import { useScrollHeader } from "@/hooks/useScrollHeader";
import { useIsMobile } from "@/hooks/useResponsive";
import { isPWAInstalled } from "@/utils/pwaUtils";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogoMark } from "./BrandLogoMark";
import { useAnnouncementBannerVisible } from "./AnnouncementBanner";

export default function Navbar() {
  const { user, userRole, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { scrollDirection, isScrolled } = useScrollHeader();
  const isBannerVisible = useAnnouncementBannerVisible();

  const isPWA = isPWAInstalled();
  const isChartsPage = location.pathname === "/charts";
  const isProfilesPage = location.pathname === "/profiles";
  const isTrackPage = location.pathname.startsWith("/tracks/");
  const isAlbumPage = location.pathname.includes("/albums/");
  const isPWASpecialPage =
    isChartsPage || isProfilesPage || isTrackPage || isAlbumPage;

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out successfully");
      navigate("/");
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Failed to sign out");
    }
  };

  const getDashboardLink = () => {
    if (!userRole) return "/dashboard";
    return `/dashboard/${userRole}`;
  };

  // Hide navbar on mobile when scrolling down
  const shouldHideNavbar = isMobile && scrollDirection === "down" && isScrolled;

  // Position navbar below banner when visible
  const navbarTop = isBannerVisible ? "top-10" : "top-0";

  // Show PWA back button on special pages
  if (isPWA && isPWASpecialPage) {
    return (
      <nav
        className={`fixed ${navbarTop} left-0 right-0 z-50 bg-navbar backdrop-blur-md border-b border-navbar-border transition-transform duration-300 ${
          shouldHideNavbar ? "-translate-y-full" : "translate-y-0"
        }`}
      >
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-navbar-foreground hover:bg-navbar-foreground/10"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>

            {/* Logo for PWA */}
            <Link to="/" className="flex items-center gap-2">
              <BrandLogoMark
                className="h-6 w-auto"
                fallbackClassName="h-6 w-6 rounded-md"
                iconSize={12}
              />
              <span className="font-archivo font-black text-base text-navbar-foreground tracking-tight">
                PayStreem
              </span>
            </Link>

            <div className="w-16"></div>
          </div>
        </div>
      </nav>
    );
  }

  // Show simplified PWA navbar for all other pages
  if (isPWA) {
    return (
      <nav
        className={`fixed ${navbarTop} left-0 right-0 z-50 bg-navbar backdrop-blur-md border-b border-navbar-border transition-transform duration-300 ${
          shouldHideNavbar ? "-translate-y-full" : "translate-y-0"
        }`}
      >
        <div className="container">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <BrandLogoMark
                className="h-8 w-auto"
                fallbackClassName="h-8 w-8 rounded-md"
                iconSize={16}
              />
              <span className="font-archivo font-black text-lg text-navbar-foreground tracking-tight">
                PayStreem
              </span>
            </Link>

            {/* Right side: Auth + Mobile Menu for PWA */}
            <div className="flex items-center gap-2">
              <AuthSection
                user={user}
                userRole={userRole}
                onSignOut={handleSignOut}
                getDashboardLink={getDashboardLink}
              />
              <MobileNavDrawer />
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={`fixed ${navbarTop} left-0 right-0 z-50 bg-navbar backdrop-blur-md border-b border-navbar-border transition-transform duration-300 ${
        shouldHideNavbar ? "-translate-y-full" : "translate-y-0"
      }`}
    >
      <div className="container">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <BrandLogoMark
              className="h-8 w-auto lg:h-10"
              fallbackClassName="h-8 w-8 lg:h-10 lg:w-10 rounded-md"
              iconSize={16}
            />
            <span className="font-archivo font-black text-lg text-navbar-foreground tracking-tight">
              PayStreem
            </span>
          </Link>

          {/* Desktop Navigation */}
          <DesktopNavigation />

          {/* Desktop Auth Section */}
          <AuthSection
            user={user}
            userRole={userRole}
            onSignOut={handleSignOut}
            getDashboardLink={getDashboardLink}
          />

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <MobileNavDrawer />
          </div>
        </div>

        {/* Mobile Menu - Removed since we use bottom navigation */}
      </div>
    </nav>
  );
}
